import { useEffect, useRef } from "react";
import { useLocation } from "wouter";
import {
  findMetaContactAnchor,
  initFacebookPixel,
  initLinkedInTag,
  trackAdPageView,
  trackMetaContact,
} from "@/lib/ad-pixels";
import { initGA, trackPageView } from "@/lib/analytics";
import { trackGTMPageView } from "@/lib/gtm";
import { useAuth } from "@/contexts/AuthContext";
import { isPublicRoute, shouldTrackPublicLocation } from "@shared/public-routes";

export { isPublicRoute, shouldTrackPublicLocation } from "@shared/public-routes";

export default function PublicPageTracker() {
  const [location] = useLocation();
  const { isAuthenticated } = useAuth();
  const prevLocation = useRef<string | null>(null);

  const gaInitialized = useRef(false);
  const refCaptured = useRef(false);

  // Capture a partner referral code (?ref=<slug>) on the first public-page load
  // and tag the visitor server-side (sets a 30-day tracking cookie). This runs
  // on the homepage now that referral links land there, and survives navigation
  // because it's stored server-side. Strip the param from the URL after capture.
  useEffect(() => {
    if (refCaptured.current) return;
    if (!isPublicRoute(location, isAuthenticated)) return;

    const params = new URLSearchParams(window.location.search);
    const refSlug = params.get("ref");
    if (!refSlug) return;

    refCaptured.current = true;
    fetch(`/api/referral/track/${encodeURIComponent(refSlug)}`).catch(() => {});

    params.delete("ref");
    const newSearch = params.toString();
    const newUrl =
      window.location.pathname + (newSearch ? `?${newSearch}` : "") + window.location.hash;
    window.history.replaceState(null, "", newUrl);
  }, [location, isAuthenticated]);

  useEffect(() => {
    if (!isPublicRoute(location, isAuthenticated)) {
      prevLocation.current = null;
      return;
    }

    // Do not load advertising tags while the user is only in the app.
    initFacebookPixel();
    initLinkedInTag();

    if (!gaInitialized.current && import.meta.env.VITE_GA_MEASUREMENT_ID) {
      initGA();
      gaInitialized.current = true;
    }

    // GTM is loaded by the standard head snippet in client/index.html; we only
    // push page_view events here, and only on public routes.
    if (!shouldTrackPublicLocation(prevLocation.current, location, isAuthenticated)) return;
    prevLocation.current = location;
    trackAdPageView();
    trackPageView(location);
    trackGTMPageView(location);
  }, [location, isAuthenticated]);

  useEffect(() => {
    const handleDocumentClick = (event: MouseEvent) => {
      if (!isPublicRoute(location, isAuthenticated)) return;
      if (findMetaContactAnchor(event.target)) trackMetaContact();
    };

    document.addEventListener("click", handleDocumentClick);
    return () => document.removeEventListener("click", handleDocumentClick);
  }, [location, isAuthenticated]);

  return null;
}
